import type { Accomplishment, DecorationCategory, DecorationKind } from '@clan-heart/shared';
import { canClaimAgain } from './claimState.js';

/** Where the viewer stands: anything, still claimable, waiting on review, or already earned at least once. */
export type ClaimFilter = 'all' | 'claimable' | 'pending' | 'earned';

export interface AccomplishmentFilters {
  search: string;
  claim: ClaimFilter;
  kind: DecorationKind | 'all';
  category: DecorationCategory | 'all';
}

export const NO_FILTERS: AccomplishmentFilters = { search: '', claim: 'all', kind: 'all', category: 'all' };

type Filterable = Pick<
  Accomplishment,
  'title' | 'description' | 'decoration' | 'category' | 'repeatable' | 'myClaim' | 'myApprovedCount'
>;

function matchesClaim(a: Filterable, claim: ClaimFilter): boolean {
  switch (claim) {
    case 'all':
      return true;
    case 'claimable':
      return canClaimAgain(a);
    case 'pending':
      return a.myClaim?.status === 'pending';
    case 'earned':
      return a.myApprovedCount > 0;
  }
}

/** The accomplishments matching every filter; search looks in the title and description, ignoring case. */
export function filterAccomplishments<T extends Filterable>(list: readonly T[], filters: AccomplishmentFilters): T[] {
  const q = filters.search.trim().toLowerCase();
  return list.filter(
    (a) =>
      (q === '' || a.title.toLowerCase().includes(q) || a.description.toLowerCase().includes(q)) &&
      matchesClaim(a, filters.claim) &&
      (filters.kind === 'all' || a.decoration === filters.kind) &&
      (filters.category === 'all' || a.category === filters.category),
  );
}

/** Reads filters from the page's query string (?q=&claim=&kind=&category=), ignoring unknown values. */
export function filtersFromParams(params: URLSearchParams): AccomplishmentFilters {
  const pick = <V extends string>(value: string | null, allowed: readonly V[], fallback: V): V =>
    value !== null && (allowed as readonly string[]).includes(value) ? (value as V) : fallback;
  return {
    search: params.get('q') ?? '',
    claim: pick(params.get('claim'), ['all', 'claimable', 'pending', 'earned'] as const, 'all'),
    kind: pick(params.get('kind'), ['all', 'ribbon', 'medal', 'ensign'] as const, 'all'),
    category: pick(
      params.get('category'),
      ['all', 'port_battles', 'fleet_actions', 'seamanship', 'shipwrighting', 'trade', 'campaigns', 'service'] as const,
      'all',
    ),
  };
}

/** The query string for `filters`, leaving out everything at its default. */
export function filtersToParams(filters: AccomplishmentFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.search.trim()) params.set('q', filters.search);
  if (filters.claim !== 'all') params.set('claim', filters.claim);
  if (filters.kind !== 'all') params.set('kind', filters.kind);
  if (filters.category !== 'all') params.set('category', filters.category);
  return params;
}
